import { useDismissProductAnnouncementAction } from '@/hooks/actions/use-dismiss-product-announcement-action';
import { isFullBleedPath } from '@/stores/nav-store';
import { useProductAnnouncementStore } from '@/stores/product-announcement-store';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { FeatureAnnouncementPanel } from './FeatureAnnouncementPanel';
import { useProductAnnouncements } from './use-product-announcements';

interface FeatureAnnouncementHostProps {
  brandId: string | null;
}

/** Selects and coordinates the product-update subset of the mixed feed. */
export function FeatureAnnouncementHost({ brandId }: FeatureAnnouncementHostProps) {
  const location = useLocation();
  const suppressed = isFullBleedPath(location.pathname);
  const activeBrandId = suppressed ? null : brandId;
  const { products } = useProductAnnouncements(activeBrandId);
  const dismiss = useDismissProductAnnouncementAction(brandId);
  const open = useProductAnnouncementStore((state) => state.open);
  const markSeen = useProductAnnouncementStore((state) => state.markSeen);
  const [selection, setSelection] = useState({ brandId, index: 0 });
  const requestedIndex = selection.brandId === brandId ? selection.index : 0;

  const index = Math.min(requestedIndex, Math.max(products.length - 1, 0));
  const current = products[index];

  useEffect(() => {
    if (selection.brandId !== brandId || selection.index !== index) {
      setSelection({ brandId, index });
    }
  }, [brandId, index, selection.brandId, selection.index]);

  // An announcement counts as read once its slide is actually on screen, which
  // means the panel has to be open. Closing the panel freezes the read state.
  useEffect(() => {
    if (!open || !activeBrandId || !current) return;
    markSeen(activeBrandId, current.id);
  }, [activeBrandId, current, markSeen, open]);

  if (suppressed || !brandId || products.length === 0) return null;

  return (
    <FeatureAnnouncementPanel
      announcement={current}
      position={index + 1}
      total={products.length}
      open={open}
      onDismiss={() => dismiss.mutate(current.id)}
      onPrevious={() =>
        setSelection(({ index: value }) => ({ brandId, index: Math.max(0, value - 1) }))
      }
      onNext={() =>
        setSelection(({ index: value }) => ({
          brandId,
          index: Math.min(products.length - 1, value + 1),
        }))
      }
      onSelect={(next) =>
        setSelection({ brandId, index: Math.min(Math.max(0, next), products.length - 1) })
      }
    />
  );
}
